/**
 * PendingApprovalsModal.tsx
 * Modal component for managers to review and approve/reject on-hold items
 * from the supervisor dashboard. Embedded version of ManagerApprovalQueue.
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { inventoryService, PendingApprovalLot } from '../../../services/inventoryService';
import { useAuth } from '../../../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  CheckCircle,
  XCircle,
  Clock,
  Package,
  RefreshCw,
  AlertTriangle,
  Check,
  X,
} from 'lucide-react';
import { formatDateTimeLocal } from '../../../utils/dateUtils';

// Map on_hold_reason codes to i18n keys
const HOLD_REASON_KEYS: Record<string, string> = {
  temperature: 'transferVerification.onHoldReasons.temperature',
  damaged: 'transferVerification.onHoldReasons.damaged',
  wrong_quantity: 'transferVerification.onHoldReasons.wrongQuantity',
  quality: 'transferVerification.onHoldReasons.quality',
  expiration: 'transferVerification.onHoldReasons.expiration',
  other: 'transferVerification.onHoldReasons.other',
};

interface PendingApprovalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingItems: PendingApprovalLot[];
  onRefresh: () => void;
  loading: boolean;
}

const PendingApprovalsModal: React.FC<PendingApprovalsModalProps> = ({
  isOpen,
  onClose,
  pendingItems,
  onRefresh,
  loading,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Confirmation modal state
  const [confirmLot, setConfirmLot] = useState<PendingApprovalLot | null>(null);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [confirmNotes, setConfirmNotes] = useState('');

  if (!isOpen) return null;


  // Get translated hold reason
  const getHoldReasonText = (reason: string) => {
    const key = HOLD_REASON_KEYS[reason];
    return key ? t(key) : reason;
  };

  // Open confirmation dialog
  const openConfirm = (lot: PendingApprovalLot, action: 'approve' | 'reject') => {
    setConfirmLot(lot);
    setConfirmAction(action);
    setConfirmNotes('');
  };

  // Close confirmation dialog
  const closeConfirm = () => {
    setConfirmLot(null);
    setConfirmAction(null);
    setConfirmNotes('');
  };

  // Handle approve action
  const handleApprove = async () => {
    if (!confirmLot || !user) return;

    setActionLoading(confirmLot.id);
    try {
      await inventoryService.approveLot(confirmLot.id, user.username || user.email || user.id, confirmNotes || undefined);
      toast.success(t('managerApproval.approved'));
      closeConfirm();
      onRefresh();
    } catch (error: any) {
      console.error('Failed to approve lot:', error);
      toast.error(t('managerApproval.approveError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle reject action
  const handleReject = async () => {
    if (!confirmLot || !user) return;

    if (!confirmNotes.trim()) {
      toast.error(t('managerApproval.rejectionNotesRequired'));
      return;
    }

    setActionLoading(confirmLot.id);
    try {
      await inventoryService.rejectLot(confirmLot.id, user.username || user.email || user.id, confirmNotes);
      toast.success(t('managerApproval.rejected'));
      closeConfirm();
      onRefresh();
    } catch (error: any) {
      console.error('Failed to reject lot:', error);
      toast.error(t('managerApproval.rejectError'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('managerApproval.title')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {pendingItems.length} {t('managerApproval.itemsPending')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Loading state */}
          {loading && pendingItems.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          )}

          {/* Empty state */}
          {!loading && pendingItems.length === 0 && (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-300 text-lg">
                {t('managerApproval.noPendingItems')}
              </p>
            </div>
          )}

          {/* Pending items list */}
          {pendingItems.length > 0 && (
            <div className="space-y-3">
              {pendingItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-amber-200 dark:border-amber-700 p-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    {/* Item info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-2">
                        <Package className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                            {item.item_english_name || item.item_number}
                            {item.item_chinese_name && (
                              <span className="text-gray-500 dark:text-gray-400 font-normal ml-2">
                                ({item.item_chinese_name})
                              </span>
                            )}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {item.item_number}
                          </p>
                        </div>
                      </div>

                      {/* Details row */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('managerApproval.transferNumber')}: </span>
                          <span className="font-mono text-gray-900 dark:text-white">{item.transfer_number}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('managerApproval.quantity')}: </span>
                          <span className="font-mono text-gray-900 dark:text-white">{Math.round(Number(item.actual_quantity))}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('managerApproval.weight')}: </span>
                          <span className="font-mono text-gray-900 dark:text-white">{parseFloat(String(item.actual_weight)).toFixed(2)} lbs</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">{t('managerApproval.receivedBy')}: </span>
                          <span className="text-gray-900 dark:text-white">{item.verified_by_name || item.verified_by}</span>
                        </div>
                      </div>

                      {/* Hold reason */}
                      <div className="mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-600" />
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                          {t('managerApproval.holdReason')}: {getHoldReasonText(item.on_hold_reason)}
                        </span>
                      </div>

                      {/* Notes if any */}
                      {item.verification_notes && (
                        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-600 p-2 rounded">
                          <span className="font-medium">{t('managerApproval.notes')}:</span> {item.verification_notes}
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => openConfirm(item, 'approve')}
                        disabled={actionLoading === item.id}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 text-sm font-medium"
                      >
                        <Check className="w-4 h-4" />
                        {t('managerApproval.approve')}
                      </button>
                      <button
                        onClick={() => openConfirm(item, 'reject')}
                        disabled={actionLoading === item.id}
                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 text-sm font-medium"
                      >
                        <X className="w-4 h-4" />
                        {t('managerApproval.reject')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirmation Dialog */}
        {confirmLot && confirmAction && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl m-4">
              <div className="flex items-center gap-3 mb-4">
                {confirmAction === 'approve' ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {confirmAction === 'approve' ? t('managerApproval.approveConfirm') : t('managerApproval.rejectConfirm')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {confirmLot.item_english_name || confirmLot.item_number}
                  </p>
                </div>
              </div>

              {/* Notes input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {confirmAction === 'approve' ? t('managerApproval.approvalNotes') : t('managerApproval.rejectionNotes')}
                </label>
                <textarea
                  value={confirmNotes}
                  onChange={(e) => setConfirmNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  rows={3}
                  placeholder={confirmAction === 'reject' ? t('managerApproval.rejectionNotesRequired') : ''}
                  required={confirmAction === 'reject'}
                />
              </div>

              {/* Confirmation buttons */}
              <div className="flex gap-3">
                <button
                  onClick={closeConfirm}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={confirmAction === 'approve' ? handleApprove : handleReject}
                  disabled={actionLoading === confirmLot.id || (confirmAction === 'reject' && !confirmNotes.trim())}
                  className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${
                    confirmAction === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {actionLoading === confirmLot.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : confirmAction === 'approve' ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('managerApproval.approve')}
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4" />
                      {t('managerApproval.reject')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingApprovalsModal;
